import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CHANNEL, OUTCOME, PURPOSE } from './callLog.constants';
import { callerFieldsOf } from './caller/callerModel';

dayjs.extend(utc);
dayjs.extend(timezone);
// Calls happen at the practice, so every call time reads in UK time.
const UK = 'Europe/London';

export function normalizeAiReview(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const arr = (v) => (Array.isArray(v) ? v : []);
  return {
    summary: typeof raw.summary === 'string' ? raw.summary : '',
    sentiment: raw.sentiment || 'Neutral',
    highlights: arr(raw.highlights),
    concerns: arr(raw.concerns),
    next_steps: arr(raw.next_steps),
  };
}

export function parseApiDate(value) {
  if (!value) return null;
  // Already ISO (starts with YYYY-)? use native.
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  // Backend format: DD-MM-YYYY HH:mm:ss(.ffffff)?(Z)?  (also tolerate a 'T' separator)
  const m = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value);
  if (m) {
    const [, dd, mm, yyyy, HH, MM, SS] = m;
    const d = new Date(`${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}Z`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

// A number as the phone system wrote it (+44 7700 900544, 07700.900.544, (0770) 090-0544). Only text with
// no real number in it (anonymous, withheld, empty) is "Unknown"; a stricter pattern used to drop
// real numbers, and Create lead then opened with an empty phone.
const externalPhone = (value) => (typeof value === 'string' && /^\+?[\d ()./-]+$/.test(value) && (value.match(/\d/g) || []).length >= 6 ? value : 'Unknown');


const leadOf = (lead) => {
  if (typeof lead === 'number' || (typeof lead === 'string' && /^\d+$/.test(lead))) return { id: lead, name: `Lead ${lead}` };
  if (!lead || typeof lead !== 'object') return null;
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
  return {
    id: lead.id,
    name: name || `Lead ${lead.id}`,
    ...(lead.phone ? { phone: lead.phone } : {}),
    // The patient's current stage (call_logs.row_data). The review window turns
    // it into the same action their Active Leads table offers.
    ...(lead.status ? { status: String(lead.status) } : {}),
    // Caller types (B4): a linked archived lead carries why it was archived.
    ...(lead.is_archived === true ? { isArchived: true, archiveReason: typeof lead.archive_reason === 'string' ? lead.archive_reason : '' } : {}),
  };
};

// Phase B contract fields (see Desktop design doc, "Phase B — API contract").
// Row: `user_name` (string|null), `purpose` (code or ""), `purpose_label`,
// `purpose_treatment_name` (string|null), `purpose_source` (transcript|ai_review|"").
// All backend-shape adaptation lives here so reconciliation is a one-file change.
// Who took the call (CU-869fnzh44): `user` (PracticeUser id|null), `user_source` ("" when no
// person), `user_desk_name` (the handset). `user_name` is the handset name when no person.
const userIdOf = (raw) => (typeof raw.user === 'number' ? raw.user : null);
const userNameOf = (raw) => (typeof raw.user_name === 'string' && raw.user_name.trim() ? raw.user_name.trim() : null);

const purposeOf = (raw) => {
  const meta = PURPOSE[raw.purpose];
  if (!meta) return { purpose: '', purposeLabel: '', purposeTreatmentName: null, purposeSource: '' };
  const treatment = typeof raw.purpose_treatment_name === 'string' && raw.purpose_treatment_name.trim() ? raw.purpose_treatment_name.trim() : null;
  return {
    purpose: meta.code,
    purposeLabel: typeof raw.purpose_label === 'string' && raw.purpose_label.trim() ? raw.purpose_label.trim() : meta.label,
    // Treatment only means something for a new-enquiry call.
    purposeTreatmentName: meta.code === PURPOSE.NEW_ENQUIRY.code ? treatment : null,
    purposeSource: typeof raw.purpose_source === 'string' ? raw.purpose_source : '',
  };
};

// Voice-integration fields (optional on every row; see callLog.constants.js).
// `provider` falls back to the channel's legacy provider so a Twilio row from
// an older backend still filters as "twilio". The others stay empty/false when
// absent so nothing downstream has to null-check.
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');
export const voiceFieldsOf = (raw, defaultProvider) => ({
  provider: str(raw.provider) || defaultProvider,
  siteNumber: str(raw.site_number),
  missedReason: str(raw.missed_reason),
  // Pattern-only hint from the server for an outbound call nobody has opened
  // (CU-869fmd3bg): the phone system reports these as answered because the
  // network answers on the recipient's behalf. A guess, not a verdict.
  likelyVoicemail: raw.likely_voicemail === true,
  followUpState: str(raw.follow_up_state),
  followUpOverdue: raw.follow_up_overdue === true,
  isTest: raw.is_test === true,
});

// Provider-neutral row from GET /api/v1/practices/{p}/calls/ (the unified Call
// Logs read model): phone systems (Elevate, ...), Twilio and WhatsApp alike.
// `id` keeps the backend form (`voice:9`, `twilio:12`, `whatsapp:3`) so the
// follow-up write-back can address the row directly. The list payload carries
// availability flags and the AI review, but not transcript bodies or the
// WhatsApp recordings list: those come from the detail read (mergeCallDetail).
const UNIFIED_OUTCOMES = new Set(Object.values(OUTCOME));
const idNumber = (id) => {
  const n = Number(String(id).split(':')[1]);
  return Number.isFinite(n) ? n : null;
};
const idOrUndefined = (value) => (Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value) : undefined);
// "Tue 15 Oct 2026, 12:25" for the follow-up note and the date fallbacks.
// UK time, like every other call time on the page (CU-869fmd2x2).
export const humanTimestamp = (iso) => (iso && dayjs(iso).isValid() ? dayjs(iso).tz(UK).format('DD MMM YYYY, HH:mm') : '');

export function normalizeUnifiedCall(raw) {
  const durationSec = typeof raw.duration === 'number' ? Math.round(raw.duration) : null;
  const recordings = raw.recording_available === true && raw.recording_url
    ? [{ id: `${raw.id}-rec`, status: 'ready', recordingUrl: raw.recording_url, transcript: null, durationMs: durationSec != null ? durationSec * 1000 : null, startedAt: raw.answered_at || null, endedAt: raw.ended_at || null }]
    : [];
  const channel = raw.channel === CHANNEL.WHATSAPP ? CHANNEL.WHATSAPP : CHANNEL.PHONE;
  // The backend emits follow_up_state on every row (default "unresponded");
  // a call-back is only owed for a missed inbound real call, so other rows
  // carry no follow-up state (same eligibility rule as the backend summary).
  const callBackOwed = raw.direction === 'inbound' && raw.outcome === OUTCOME.MISSED && raw.is_test !== true;
  const timestamp = parseApiDate(raw.started_at);
  const aiReview = normalizeAiReview(raw.ai_review);
  return {
    id: String(raw.id),
    channel,
    direction: raw.direction,
    contact: externalPhone(raw.direction === 'inbound' ? raw.from_number : raw.to_number),
    numberLabel: str(raw.site_label) || str(raw.site_number),
    outcome: UNIFIED_OUTCOMES.has(raw.outcome) ? raw.outcome : OUTCOME.UNKNOWN,
    status_raw: str(raw.state),
    durationSec,
    timestamp,
    timestampHuman: humanTimestamp(timestamp),
    lead: leadOf(raw.lead),
    userName: userNameOf(raw),
    user: userIdOf(raw),
    userSource: str(raw.user_source),
    userDeskName: str(raw.user_desk_name),
    ...purposeOf(raw),
    ...voiceFieldsOf(raw, channel === CHANNEL.WHATSAPP ? 'whatsapp' : 'twilio'),
    followUpState: callBackOwed ? str(raw.follow_up_state) : '',
    followUpOverdue: callBackOwed && raw.follow_up_overdue === true,
    huntGroup: str(raw.hunt_group),
    ringingSeconds: typeof raw.ringing_seconds === 'number' ? raw.ringing_seconds : null,
    followUpDueAt: raw.follow_up_due_at || null,
    followUpAttempts: typeof raw.follow_up_attempts === 'number' ? raw.follow_up_attempts : 0,
    respondedAt: raw.responded_at || null,
    // Assign / change contact goes through the matching routes (callMatching.api.js
    // matchingUrlFor). The backend refuses WhatsApp calls ("not assignable"), so
    // those rows offer no button, as before this page was server-paged.
    assignable: channel !== CHANNEL.WHATSAPP,
    siteLabel: str(raw.site_label),
    // The line's own name and number (CU-869fn2wcn); '' from an older backend.
    lineLabel: str(raw.line_label),
    siteNumber: str(raw.site_number),
    recordings,
    recordingCount: recordings.length,
    hasRecording: raw.recording_available === true,
    recordingState: str(raw.recording_state),
    // Why there is no recording (contract ~/pm/contracts/B3.md §4, CU-869fkj67y):
    // recording_status available / fetching / unavailable / none, and recording_reason
    // (line_not_set_up, older_than_30_days, not_recorded_ring_group, not_answered, ...).
    recordingStatus: str(raw.recording_status),
    recordingReason: str(raw.recording_reason),
    // Server-computed row buttons (caller types backend, CU-869fm5hhm). null = an
    // older backend; the table then falls back to its own rules.
    nextActions: Array.isArray(raw.next_actions) ? raw.next_actions : null,
    ...callerFieldsOf(raw),
    // The list carries only the flag; the text arrives with the detail read.
    hasTranscript: raw.transcript_available === true,
    transcriptAvailable: raw.transcript_available === true,
    leadMatch: raw.matching_state ? { state: raw.matching_state, reason: raw.matching_reason, version: raw.matching_version } : null,
    primaryRecording: recordings[0] || null,
    transcript: null,
    aiReview,
    aiReviewAvailable: raw.ai_review_available === true || Boolean(aiReview),
    aiReviewGeneratedAt: raw.ai_review_generated_at || null,
    aiSummarySource: str(raw.ai_summary_source),
    // B7 pipeline state, same field names the lead page reads
    // (leadDetail/deskCalls/leadDeskCalls.api.js). summary_status is one of
    // available | waiting_for_elevate | queued | not_available | none; without
    // it the window can only say "no summary" when the truth is "not yet".
    summarySource: str(raw.summary_source),
    summaryStatus: str(raw.summary_status) || 'none',
    transcriptStatus: str(raw.transcript_status) || 'none',
    aiTopics: Array.isArray(raw.ai_topics) ? raw.ai_topics.map((t) => str(t?.topic)).filter(Boolean) : [],
    // Ids behind the Generate transcript / AI review buttons (Twilio and
    // WhatsApp only; voice rows have no phone_id, so no button).
    _refs: { callId: idNumber(raw.id), rowId: String(raw.id), source: String(raw.id).split(':')[0], phoneId: idOrUndefined(raw.phone_id), businessId: idOrUndefined(raw.business_id) },
  };
}

const detailRecording = (rec) => ({
  id: rec.id,
  status: rec.status,
  recordingUrl: rec.recording_url || null,
  transcript: typeof rec.transcript_text === 'string' && rec.transcript_text.trim() ? rec.transcript_text : null,
  durationMs: typeof rec.duration_ms === 'number' ? rec.duration_ms : null,
  startedAt: rec.started_at || null,
  endedAt: rec.ended_at || null,
});

// Review modal: the list row plus GET calls/{id}/ (transcript, and every
// WhatsApp recording with its own proxied `recording=<id>` URL). Returns the
// row unchanged until the detail arrives.
export function mergeCallDetail(row, detail) {
  if (!row || !detail || typeof detail !== 'object') return row;
  const transcript = typeof detail.transcript === 'string' && detail.transcript.trim() ? detail.transcript : null;
  let recordings = row.recordings;
  if (Array.isArray(detail.recordings) && detail.recordings.length) {
    recordings = detail.recordings.map(detailRecording);
  } else if (transcript) {
    // Same shape the page always gave a saved transcript: carried on the one recording
    // (with no audio url when the call has none).
    recordings = row.recordings.length
      ? row.recordings.map((rec, index) => (index === 0 ? { ...rec, transcript } : rec))
      : [{ id: `${row.id}-rec`, status: 'ready', recordingUrl: null, transcript, durationMs: row.durationSec != null ? row.durationSec * 1000 : null, startedAt: null, endedAt: null }];
  }
  const primary = recordings.find((rec) => rec.status === 'ready' && rec.recordingUrl) || recordings[0] || null;
  const aiReview = normalizeAiReview(detail.ai_review) || row.aiReview;
  return {
    ...row,
    recordings,
    recordingCount: recordings.filter((rec) => rec.recordingUrl).length,
    primaryRecording: primary,
    transcript: transcript || primary?.transcript || null,
    hasTranscript: row.hasTranscript || Boolean(transcript) || recordings.some((rec) => rec.transcript),
    aiReview,
    aiReviewGeneratedAt: detail.ai_review_generated_at || row.aiReviewGeneratedAt,
    // Call review window (CU-869fmd2x2): UK number, "site · ring group", and the hints read
    // from the summary with their text offsets (raw; callReviewModel.readExtracted reads it).
    numberDisplay: str(detail.number_display) || row.numberDisplay || '',
    lineDisplay: str(detail.line_display) || row.lineDisplay || '',
    extracted: detail.extracted && typeof detail.extracted === 'object' ? detail.extracted : null,
    // The detail read is the freshest view of the pipeline while it is running.
    summarySource: str(detail.summary_source) || row.summarySource,
    summaryStatus: str(detail.summary_status) || row.summaryStatus,
    transcriptStatus: str(detail.transcript_status) || row.transcriptStatus,
    // Which "Speaker A"/"Speaker B" is the practice (apps/voice/speakers.py). Absent
    // on an older backend, and absent by design when the two sides can't be told
    // apart — the reader falls back to the provider's own labels either way.
    transcriptSpeakers:
      detail.transcript_speakers && typeof detail.transcript_speakers === 'object'
        ? detail.transcript_speakers
        : row.transcriptSpeakers || null,
  };
}
